import {
  BarChart3,
  Briefcase,
  CalendarClock,
  ChevronLeft,
  ClipboardList,
  Cog,
  Database,
  FileText,
  GraduationCap,
  Home,
  KeyRound,
  LayoutGrid,
  ListChecks,
  MessageCircle,
  MessageSquare,
  Palette,
  Plug,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Store,
  Users,
} from "lucide-react";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { useAuth } from "@/auth/useAuth";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  usePendingInstallRequests,
  usePendingPublishRequests,
} from "@/features/agents/hooks/useAgentInstall";
import { useHitlRequests } from "@/features/hitl/hooks/useHitl";
import { cn } from "@/lib/utils";
import { useUiStore } from "@/stores/uiStore";

interface NavCtx {
  isAdmin: boolean;
  hasManager: boolean;
  hasCreator: boolean;
}

interface NavItem {
  to: string;
  labelKey: string;
  icon: ComponentType<{ className?: string }>;
  /** Visibilidade por item; sobrepoe a da secao. Default: section.visible. */
  visible?: (ctx: NavCtx) => boolean;
}

interface NavSection {
  labelKey: string;
  items: NavItem[];
  visible: (ctx: NavCtx) => boolean;
}

// Labels referenciam chaves de ``common:nav.sections.*`` / ``common:nav.items.*``
// para que o LanguageToggle reflita troca de idioma em tempo real.
const sections: NavSection[] = [
  {
    labelKey: "nav.sections.workspace",
    visible: () => true,
    items: [
      { to: "/", labelKey: "nav.home", icon: Home },
      { to: "/me/agents", labelKey: "nav.items.myAgents", icon: Sparkles },
      { to: "/marketplace", labelKey: "nav.items.marketplace", icon: Store },
      { to: "/threads", labelKey: "nav.items.threads", icon: MessageSquare },
      // M8: chat direto com LLM (sem agente). Visivel pra todos.
      { to: "/chat", labelKey: "nav.items.directChat", icon: MessageCircle },
      {
        to: "/sessions",
        labelKey: "nav.items.teamSessions",
        icon: LayoutGrid,
      },
      // Tasks em background + Scheduler: visiveis a todos (cada um ve
      // as suas; admin ve tudo -- filtro no backend).
      { to: "/tasks", labelKey: "nav.items.tasks", icon: ClipboardList },
      {
        to: "/scheduler",
        labelKey: "nav.items.scheduler",
        icon: CalendarClock,
      },
      {
        to: "/me/pending-approvals",
        labelKey: "nav.items.approvals",
        icon: ListChecks,
        // Apenas quem pode aprovar (manager do owner_team OR admin) ve.
        // 'user' e 'creator' nao tem o que aprovar.
        visible: ({ isAdmin, hasManager }) => isAdmin || hasManager,
      },
      // M7: meu uso pessoal (admin tambem ve, mas usa /observability geral).
      {
        to: "/observability/me",
        labelKey: "nav.items.myUsage",
        icon: BarChart3,
      },
    ],
  },
  {
    // 'Recursos do time' inclui Times/KBs/MCPs. Creator precisa ver
    // KBs/MCPs do time para vincula-los a agentes que cria. Manager
    // gerencia tudo. Admin tambem.
    labelKey: "nav.sections.teamResources",
    visible: ({ hasManager, hasCreator, isAdmin }) =>
      hasManager || hasCreator || isAdmin,
    items: [
      {
        to: "/teams",
        labelKey: "nav.items.teams",
        icon: Briefcase,
        // Apenas manager/admin gerenciam times -- creator nao.
        visible: ({ isAdmin, hasManager }) => isAdmin || hasManager,
      },
      {
        to: "/knowledge-bases",
        labelKey: "nav.items.knowledgeBases",
        icon: Database,
      },
      { to: "/mcps", labelKey: "nav.items.mcps", icon: Plug },
      { to: "/skills", labelKey: "nav.items.skills", icon: GraduationCap },
    ],
  },
  {
    labelKey: "nav.sections.admin",
    visible: ({ isAdmin }) => isAdmin,
    items: [
      { to: "/users", labelKey: "nav.items.users", icon: Users },
      {
        to: "/admin/service-users",
        labelKey: "nav.items.serviceUsers",
        icon: KeyRound,
      },
      { to: "/llm-models", labelKey: "nav.items.llmModels", icon: Cog },
      {
        to: "/company-context",
        labelKey: "nav.items.companyContext",
        icon: FileText,
      },
      {
        to: "/admin/prompt-tones",
        labelKey: "nav.items.promptTones",
        icon: Palette,
      },
      { to: "/pii", labelKey: "nav.items.piiFilter", icon: ShieldAlert },
      {
        to: "/admin/audit-log",
        labelKey: "nav.items.auditLog",
        icon: ShieldCheck,
      },
      {
        to: "/observability",
        labelKey: "nav.items.observability",
        icon: Shield,
      },
    ],
  },
];

export function Sidebar() {
  const { t } = useTranslation("common");
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const { isAdmin, memberships } = useAuth();
  const hasManager = memberships.some((m) => m.role === "manager");
  const hasCreator = memberships.some((m) => m.role === "creator");
  const navCtx = { isAdmin, hasManager, hasCreator };
  const canApprove = isAdmin || hasManager;

  // Soma os 3 sources (HITL + Install + Publish) para o badge.
  // 'user' e 'creator' nao chamam esses endpoints (nao ve a aba).
  const hitl = useHitlRequests();
  const publish = usePendingPublishRequests();
  const install = usePendingInstallRequests();
  const approvalsCount = canApprove
    ? (hitl.data?.length ?? 0) +
      (publish.data?.length ?? 0) +
      (install.data?.length ?? 0)
    : 0;

  return (
    <aside
      className={cn(
        "sticky top-0 flex h-screen shrink-0 flex-col border-r border-line bg-surface text-ink-900 transition-[width] duration-200",
        collapsed ? "w-[72px]" : "w-[260px]",
      )}
    >
      <div
        className={cn(
          "flex h-14 shrink-0 items-center border-b border-line",
          collapsed ? "justify-center px-2" : "justify-between px-3",
        )}
      >
        {!collapsed ? (
          <div className="flex items-center gap-2">
            {/*
              Logica negativa pra contrastar: tema CLARO mostra logo
              DARK; tema ESCURO mostra logo LIGHT. O par ``block`` /
              ``dark:hidden`` + ``hidden`` / ``dark:block`` segue a
              classe ``dark`` que o uiStore aplica no <html>.
            */}
            <img
              src="/orbit_dark.svg"
              alt=""
              aria-hidden="true"
              className="block h-7 w-7 shrink-0 dark:hidden"
            />
            <img
              src="/orbit_light.svg"
              alt=""
              aria-hidden="true"
              className="hidden h-7 w-7 shrink-0 dark:block"
            />
            <span className="text-base font-semibold tracking-tight text-ink-950">
              Orbit
            </span>
          </div>
        ) : (
          // Quando colapsada, o proprio logo eh o trigger para expandir --
          // evita empilhar logo + botao no header estreito (72px).
          <button
            type="button"
            onClick={toggle}
            aria-label={t("nav.expandSidebar")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors hover:bg-ink-300/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/40 dark:hover:bg-white/[0.06]"
          >
            <img
              src="/orbit_dark.svg"
              alt=""
              aria-hidden="true"
              className="block h-7 w-7 shrink-0 dark:hidden"
            />
            <img
              src="/orbit_light.svg"
              alt=""
              aria-hidden="true"
              className="hidden h-7 w-7 shrink-0 dark:block"
            />
          </button>
        )}
        {!collapsed ? (
          <button
            type="button"
            onClick={toggle}
            aria-label={t("nav.collapseSidebar")}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-ink-500 transition-colors hover:bg-ink-300/20 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/40 dark:hover:bg-white/[0.06] dark:hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <nav
        className={cn(
          "scrollbar-clean flex flex-1 flex-col gap-5 overflow-y-auto py-4",
          collapsed ? "px-2" : "px-3",
        )}
      >
        {sections
          .filter((section) => section.visible(navCtx))
          .map((section, idx) => {
            const visibleItems = section.items.filter(
              (item) => item.visible?.(navCtx) ?? true,
            );
            if (visibleItems.length === 0) return null;
            return (
              <div key={section.labelKey} className="flex flex-col gap-1">
                {!collapsed ? (
                  <span className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
                    {t(section.labelKey)}
                  </span>
                ) : idx > 0 ? (
                  <Separator className="my-1" />
                ) : null}
                {visibleItems.map((item) => {
                  const showBadge =
                    item.to === "/me/pending-approvals" && approvalsCount > 0;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      className={({ isActive }) =>
                        cn(
                          "group relative flex items-center rounded-xl text-[13px] font-medium transition-colors",
                          collapsed
                            ? "h-11 w-11 justify-center"
                            : "gap-3 px-3 py-2.5",
                          isActive
                            ? "bg-brand-50 text-brand-700 dark:bg-brand-500/12 dark:text-white"
                            : "text-ink-700 hover:bg-ink-300/20 hover:text-ink-950 dark:text-white/65 dark:hover:bg-white/[0.06] dark:hover:text-white",
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon
                            className={cn(
                              "h-4 w-4 shrink-0 transition-colors",
                              isActive
                                ? "text-brand-500"
                                : "text-ink-500 group-hover:text-ink-900 dark:group-hover:text-white",
                            )}
                          />
                          {!collapsed ? (
                            <span className="flex flex-1 items-center justify-between gap-2 truncate">
                              <span className="truncate">
                                {t(item.labelKey)}
                              </span>
                              {showBadge ? (
                                <Badge className="bg-brand-500 px-1.5 py-0 text-[10px] tracking-normal text-white">
                                  {approvalsCount}
                                </Badge>
                              ) : null}
                            </span>
                          ) : showBadge ? (
                            <Badge className="absolute -right-1 -top-1 h-4 min-w-[1rem] bg-brand-500 px-1 py-0 text-[10px] tracking-normal text-white">
                              {approvalsCount}
                            </Badge>
                          ) : null}
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
      </nav>
    </aside>
  );
}
